import Phaser from 'phaser';
import { createAthleteView, type AthleteView } from './AthleteView';
import type { AiOpponent } from './data/aiRoster';

/**
 * A computer-controlled sprinter: an athlete view placed in its own lane that paces
 * itself to reach the finish at a (noised) target time, so the player has a field to
 * race against. Distance is a mild ease-in curve; the rig animates by current speed.
 */
export class AiRunner {
  readonly opponent: AiOpponent;
  readonly view: AthleteView;
  readonly finishTime: number;
  distance = 0;
  finished = false;

  private prevDistance = 0;
  private readonly raceMeters: number;
  private readonly pixelsPerMeter: number;
  private readonly startX: number;

  constructor(
    scene: Phaser.Scene,
    opponent: AiOpponent,
    finishTime: number,
    laneY: number,
    displayHeight: number,
    startX: number,
    raceMeters: number,
    pixelsPerMeter: number,
  ) {
    this.opponent = opponent;
    this.finishTime = finishTime;
    this.raceMeters = raceMeters;
    this.pixelsPerMeter = pixelsPerMeter;
    this.startX = startX;
    this.view = createAthleteView(scene, startX, laneY, { skin: opponent.skin }, displayHeight);
  }

  /** Advance the AI to its position at `elapsed` seconds. */
  update(elapsed: number, dt: number): void {
    const p = Phaser.Math.Clamp(elapsed / this.finishTime, 0, 1);
    this.distance = this.raceMeters * Math.pow(p, 1.15);
    if (p >= 1) this.finished = true;

    const dDist = Math.max(0, this.distance - this.prevDistance);
    this.prevDistance = this.distance;
    const avgSpeed = this.raceMeters / this.finishTime;
    const speedNorm = dt > 0 ? Phaser.Math.Clamp(dDist / dt / (avgSpeed * 1.5), 0, 1) : 0;

    this.view.container.x = this.startX + this.distance * this.pixelsPerMeter;
    this.view.setMotion(this.finished ? 0 : speedNorm);
    this.view.update(dt);
    if (this.finished) this.view.setPose('idle');
  }
}
